import type { AuthTokens, User } from './types';

const KEYS = {
  ACCESS_TOKEN: 'auth_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'auth_v2_user',
} as const;

export class StorageAdapter {
  public getTokens(): AuthTokens | null {
    try {
      let accessTokenRaw = localStorage.getItem(KEYS.ACCESS_TOKEN);
      let refreshToken = localStorage.getItem(KEYS.REFRESH_TOKEN) || '';
      if (!accessTokenRaw) {
        accessTokenRaw =
          localStorage.getItem('auth_v2_accessToken') ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('token') ||
          '';
      }
      if (!refreshToken) {
        refreshToken =
          localStorage.getItem('auth_v2_refreshToken') ||
          localStorage.getItem('refreshToken') ||
          '';
      }

      // Only accessToken is required (refreshToken is stored in HTTP-only cookie)
      const accessToken = accessTokenRaw ? String(accessTokenRaw).trim() : '';
      const lower = accessToken.toLowerCase();
      const isJwtLike = accessToken.split('.').length >= 3;
      // ✅ ENTERPRISE FIX: Treat "null"/"undefined"/malformed tokens as missing and purge them
      if (!accessToken || lower === 'null' || lower === 'undefined' || !isJwtLike) {
        try {
          localStorage.removeItem(KEYS.ACCESS_TOKEN);
        } catch (_) {}
        return null;
      }

      return { accessToken, refreshToken };
    } catch (error) {
      console.error('StorageAdapter: Failed to read tokens', error);
      return null;
    }
  }

  public setTokens(tokens: AuthTokens): void {
    try {
      localStorage.setItem(KEYS.ACCESS_TOKEN, tokens.accessToken);
      localStorage.setItem(KEYS.REFRESH_TOKEN, tokens.refreshToken);
      // Legacy compatibility for existing consumers
      localStorage.setItem('auth_v2_accessToken', tokens.accessToken);
      localStorage.setItem('auth_v2_refreshToken', tokens.refreshToken);
    } catch (error) {
      console.error('StorageAdapter: Failed to write tokens', error);
    }
  }

  public getUser(): User | null {
    try {
      const userJson = localStorage.getItem(KEYS.USER);
      if (!userJson) {
        return null;
      }
      return JSON.parse(userJson);
    } catch (error) {
      console.error('StorageAdapter: Failed to read user', error);
      return null;
    }
  }

  public setUser(user: User): void {
    try {
      localStorage.setItem(KEYS.USER, JSON.stringify(user));
    } catch (error) {
      console.error('StorageAdapter: Failed to write user', error);
    }
  }

  public clear(): void {
    try {
      localStorage.removeItem(KEYS.ACCESS_TOKEN);
      localStorage.removeItem(KEYS.REFRESH_TOKEN);
      localStorage.removeItem(KEYS.USER);
      // Clear sessionStorage mirrors as well (if present).
      try {
        sessionStorage.removeItem(KEYS.ACCESS_TOKEN);
        sessionStorage.removeItem(KEYS.REFRESH_TOKEN);
        sessionStorage.removeItem(KEYS.USER);
      } catch (_) {}
      this.clearLegacyKeys();
    } catch (error) {
      console.error('StorageAdapter: Failed to clear storage', error);
    }
  }

  public migrateLegacyKeys(): void {
    try {
      if (localStorage.getItem(KEYS.ACCESS_TOKEN)) {
        console.log('StorageAdapter: Already migrated to v2 keys');
        return;
      }

      console.log('StorageAdapter: Migrating legacy keys...');

      const accessToken =
        localStorage.getItem('auth_v2_accessToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('token');

      if (accessToken) {
        const t = String(accessToken).trim();
        const lower = t.toLowerCase();
        const isJwtLike = t.split('.').length >= 3;
        if (t && lower !== 'null' && lower !== 'undefined' && isJwtLike) {
          localStorage.setItem(KEYS.ACCESS_TOKEN, t);
        }
      }

      const refreshToken =
        localStorage.getItem('auth_v2_refreshToken') || localStorage.getItem('refreshToken');
      if (refreshToken) {
        localStorage.setItem(KEYS.REFRESH_TOKEN, refreshToken);
      }

      const userJson =
        localStorage.getItem('user') ||
        localStorage.getItem('userData') ||
        localStorage.getItem('authUser') ||
        localStorage.getItem('currentUser');

      if (userJson) {
        localStorage.setItem(KEYS.USER, userJson);
      }

      this.clearLegacyKeys();

      console.log('StorageAdapter: Migration complete');
    } catch (error) {
      console.error('StorageAdapter: Migration failed', error);
    }
  }

  private clearLegacyKeys(): void {
    console.log('StorageAdapter: Cleaning up legacy auth keys...');

    const legacyKeys = [
      'auth_v2_accessToken',
      'auth_v2_refreshToken',
      'accessToken',
      'authToken',
      'token',
      'refreshToken',
      'authData',
      'auth_v2_user',
      'user',
      'userData',
      'authUser',
      'currentUser',
      'tenantId',
    ];

    let removed = 0;
    legacyKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        removed++;
      }
    });

    if (removed > 0) {
      console.log(`StorageAdapter: Removed ${removed} legacy keys`);
    }
  }
}
